import type React from 'react';
import {Box, Link, Paper, Stack, Typography} from '@mui/material';
import {OpenInNew} from '@mui/icons-material';
import {Tag} from '~/components/tag/Tag';
import type {ExternalSignal, SignalOptions} from '../../types/signalsTypes';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const IMPACT_COLOR = {high: 'error', medium: 'warning', low: 'default'} as const;
const SENTIMENT = {negative: '▼ Negative', neutral: '● Neutral', positive: '▲ Positive'} as const;
const WHEN = new Intl.DateTimeFormat(LOCALE, {day: 'numeric', month: 'short', year: 'numeric'});

type Props = {signal: ExternalSignal; options: SignalOptions; alert: boolean};

/** One signal in the Suppliers 360 signal-card pattern: publisher, impact, category, sentiment, relevance, language. */
export const SignalCard: React.FC<Props> = ({signal: s, options, alert}) => (
    <Paper variant="outlined" sx={{p: 1.5, borderLeft: '3px solid', borderLeftColor: alert ? 'error.main' : 'divider'}}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Box aria-hidden sx={{width: 22, height: 22, borderRadius: 1, bgcolor: 'grey.200', display: 'grid', placeItems: 'center',
                fontSize: 11, fontWeight: 700}}>
                {(s.publisher || '?').slice(0, 1).toUpperCase()}
            </Box>
            <Typography variant="caption" color="text.secondary">
                {`${s.publisher || 'Unknown publisher'} · ${WHEN.format(new Date(s.publishedAt))} · ${s.language.toUpperCase()}`}
            </Typography>
            <Box sx={{flex: 1}}/>
            <Tag label={`${s.impact[0].toUpperCase()}${s.impact.slice(1)} impact`} color={IMPACT_COLOR[s.impact]} solid={false}/>
            <Tag label={options.impactTypes.find(t => t.value === s.impactType)?.label ?? s.impactType} color="default" solid={false}/>
            {s.eventCategory && <Tag label={s.eventCategory} color="info" solid={false}/>}
        </Stack>
        <Typography variant="subtitle2" sx={{mt: 0.75}}>
            {s.url ? (
                <Link href={s.url} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit">
                    {s.title} <OpenInNew sx={{fontSize: 14, verticalAlign: 'text-top'}}/>
                </Link>
            ) : s.title}
        </Typography>
        {s.summary && <Typography variant="body2" color="text.secondary" sx={{mt: 0.25}}>{s.summary}</Typography>}
        {(s.exposure || s.rationale) && (
            <Stack gap={0.25} sx={{mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1}}>
                {s.exposure && <Typography variant="caption"><b>Exposure: </b>{s.exposure}</Typography>}
                {s.rationale && <Typography variant="caption"><b>Why it matters: </b>{s.rationale}</Typography>}
            </Stack>
        )}
        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.75}}>
            {`${SENTIMENT[s.sentiment]} · Relevance ${s.relevance}${s.source === 'manual' ? ' · Added by Mithra' : ''}`}
        </Typography>
    </Paper>
);
